import { getMealTypeTimes } from '$tablet-app/src/app/calendar/meals/actions';
import {
  createMealFromRecipe,
  RecommendedRecipes
} from '$tablet-app/src/app/recipes/models';
import { Recipe } from '$tablet-app/src/app/recipes/types';
import { selectRandomItemWeightedByOrder } from '$tablet-app/src/app/utils/randomisation';
import { MealType } from '@akin-home/amplify';
import { OptionModel } from '@akin-home/options';
import { MealAvoidanceDegree, MealPlanPreferences } from '@akin-home/recipes';
import {
  getRecipeDietaryBalance,
  getRecipeIngredients,
  isRecipeWithinUsersTolerances,
  mapAllergensToRecipes
} from './recipeFiltering';

export const paramsMap = {
  skillLevel: {
    Beginner: 1,
    'Some practice needed': 2,
    'Confident Cooks': 3,
  },
  prepTime: {
    'Under 10 minutes': 0,
    '15 minutes': 1,
    '30 minutes': 2,
    '45 minutes': 3,
    '60+ minutes': 4,
  },
  budget: {
    '0': 0,
    '1': 1,
    '2': 2,
    '3': 3,
    '4': 4,
  },
  time_and_effort: {
    '0': 0,
    '1': 1,
    '2': 2,
    '3': 3,
    '4': 4,
  },
  dietary_balance: {
    Vegan: 1,
    Vegetarian: 2,
    'Mix it up': 3,
    Fish: 3,
    'Meat/Fish': 4,
  },
};

const factorWeightings = {
  dietary_balance: 1,
};

export const getParamMapWithAvgs = () => {
  const paramMapWithAvg: any = { ...paramsMap };
  Object.keys(paramsMap).forEach((k) => {
    const values = Object.values(paramsMap[k]) as any[];
    const avg =
      values.reduce((a, b) => Number(a) + Number(b), 0) / values.length;
    paramMapWithAvg[k]['avg'] = Math.round(avg);
  });
  return paramMapWithAvg;
};

export const paramMapWithAvgs = getParamMapWithAvgs();

const selectRecipe = (recipes: any[]) =>
  selectRandomItemWeightedByOrder(recipes, 'distToUserPref');

export const makeRecommended = (
  baseRecommendedMeal: any,
  recommendedRecipes: RecommendedRecipes,
  mealType: MealType,
  date: Date,
  exclRecipesFromTheseMeals: any[]
) => {
  const exclTheseRecipeIds =
    (exclRecipesFromTheseMeals &&
      exclRecipesFromTheseMeals.map(
        (meal) => meal.recipes && meal.recipes.length > 0 && meal.recipes[0].id
      )) ||
    [];
  const recommendedRecipesWithoutExcls = recommendedRecipes[mealType].filter(
    (rec) => !exclTheseRecipeIds.includes(rec.id)
  );

  if (!recommendedRecipesWithoutExcls?.length) return [];

  const selectedRecipe: Recipe = selectRecipe(
    recommendedRecipesWithoutExcls
  ) as Recipe;
  
  const { start, end } = getMealTypeTimes(mealType as MealType, date);

  const recommendedMeal = createMealFromRecipe(selectedRecipe, {
    mealType,
    ...baseRecommendedMeal,
    start: new Date(start),
    end: new Date(end),
    recommended: true,
  });
  return [recommendedMeal] || [];
};

export const calc_euclidean_dist = (factors1: any, factors2: any) => {
  let distance = 0;
  Object.keys(factors1).forEach((factor) => {
    const factorWeighting: number = (factorWeightings as any)[factor] || 1;
    distance += Math.pow(
      (factors1[factor] - factors2[factor]) * factorWeighting,
      2
    );
  });
  return Math.sqrt(distance);
};

export const fetchRecommendedRecipes = (
  recipes: Recipe[],
  allergenOptions: OptionModel[],
  userPreferences: MealPlanPreferences
) => {
  const prunedRecipes: Recipe[] = [];

  const userAllergies =
    userPreferences.allergies?.map((allergy) => allergy.toLowerCase()) || [];

  const userTotalAvoidances =
    userPreferences.avoidances
      ?.filter((avoid) => avoid.degree === MealAvoidanceDegree.ALL_OF_THE_TIME)
      .map((a) => a.category.toLowerCase()) || [];

  const userPartialAvoidances =
    userPreferences.avoidances
      ?.filter((avoid) => avoid.degree === MealAvoidanceDegree.SOME_OF_THE_TIME)
      .map((a) => a.category.toLowerCase()) || [];

  userPartialAvoidances.forEach((partialAvoidance) => {
    userPreferences[partialAvoidance] = 0;
  });

  delete userPreferences.allergies;
  delete userPreferences.avoidances;

  // need to chat about how to handle people factor
  delete userPreferences.people;

  const recipesWithAllergens = mapAllergensToRecipes(recipes, allergenOptions);

  recipesWithAllergens.forEach((recipe: Recipe) => {
    const recipeSpecialDiets =
      recipe.specialDiets?.map((sd) => sd.label.toLowerCase().trim()) || [];

    const elementaryIngredients = getRecipeIngredients(recipe) || [];

    const recipeWithinUserTolerances = isRecipeWithinUsersTolerances(
      recipe.allergens || [],
      recipeSpecialDiets,
      elementaryIngredients,
      userAllergies,
      userTotalAvoidances,
      userPartialAvoidances
    );

    if (!recipeWithinUserTolerances) return;

    const recipeFactors = mapRecipeParamsToFactors(
      { ...recipe },
      paramMapWithAvgs
    );

    const dietBalanceAsDiscreteType = true;
    recipeFactors.dietary_balance = getRecipeDietaryBalance(
      dietBalanceAsDiscreteType,
      elementaryIngredients,
      recipeSpecialDiets
    );

    const userPrefSubset = {
      dietary_balance: userPreferences.dietary_balance,
    };

    const recipeFactorsSubset = {
      dietary_balance: recipeFactors.dietary_balance,
    };

    const distToUserPref = calc_euclidean_dist(
      userPrefSubset,
      recipeFactorsSubset
    );

    recipe.distToUserPref = distToUserPref;
    recipe.dietary_balance = recipeFactors.dietary_balance;
    recipe.user_dietary_balance = userPrefSubset.dietary_balance;

    prunedRecipes.push(recipe);
  });

  return {
    Breakfast: prunedRecipes.filter((recipe) =>
      recipe.mealTypes.includes(MealType.Breakfast)
    ),
    Lunch: prunedRecipes.filter((recipe) =>
      recipe.mealTypes.includes(MealType.Lunch)
    ),
    Dinner: prunedRecipes.filter((recipe) =>
      recipe.mealTypes.includes(MealType.Dinner)
    ),
    Snack: prunedRecipes.filter((recipe) =>
      recipe.mealTypes.includes(MealType.Snack)
    ),
  };
};

const mapRecipeParamsToFactors = (params: any, paramMappings: any) => {
  let factors: any = {};

  Object.keys(paramMappings).forEach((k) => {
    if (Object.keys(params).includes(k) && params[k]) {
      if (Object.keys(params) && Object.keys(params[k]).includes('label')) {
        const label = params[k]['label'];
        factors[k] = paramMappings[k][label];
      } else if (Object.keys(paramMappings[k]).includes('avg')) {
        factors[k] = paramMappings[k]['avg'];
      }
    } else {
      factors[k] = paramMappings[k]['avg'];
    }
  });

  return factors;
};

export const fillMealPlanPreferences = (inputPreferencesObj: any) => {
  let mappedPreferences: any;
  const userKeys = Object.keys(inputPreferencesObj);
  const mappedParamKeys = Object.keys(paramMapWithAvgs);

  mappedParamKeys.forEach((k) => {
    if (userKeys.includes(k)) {
      mappedPreferences = {
        ...mappedPreferences,
        [k]: inputPreferencesObj[k],
      };
    }
    if (
      !mappedPreferences ||
      mappedPreferences[k] === undefined ||
      mappedPreferences[k] === null
    ) {
      mappedPreferences = {
        ...mappedPreferences,
        [k]: paramMapWithAvgs[k]['avg'],
      };
    }
  });
  const timeAndEffort = inputPreferencesObj['time_and_effort'];
  if (timeAndEffort) {
    mappedPreferences['skillLevel'] = timeAndEffort;
    mappedPreferences['prepTime'] = timeAndEffort;
  }
  const avoidances = inputPreferencesObj['avoidances'];
  mappedPreferences['avoidances'] = avoidances || [];

  const allergies = inputPreferencesObj['allergies'];
  mappedPreferences['allergies'] = allergies || [];

  const people = inputPreferencesObj['people'];
  mappedPreferences['people'] = people || 1;

  return mappedPreferences;
};
